/**
 * BrandAssetsPage — central library for logos, e-signatures, social icons.
 *
 * Upload once, edit anywhere. Assets appear automatically in the WYSIWYG
 * editor's Assets menu (see `RichTextEditor.js`) so marketers never have to
 * look up URLs manually.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import MarketingShell from "./MarketingShell";
import api from "@/lib/api";
import { resolveBackendFileUrl } from "@/lib/fileUrls";
import { Plus, Trash, PencilSimple, UploadSimple, X, Link as LinkIconPh } from "@phosphor-icons/react";
import { toast } from "sonner";

const ASSET_TYPES = [
  { value: "logo", label: "Logo" },
  { value: "signature", label: "Signature" },
  { value: "social", label: "Social icon" },
  { value: "icon", label: "Icon" },
  { value: "banner", label: "Banner" },
  { value: "other", label: "Other" },
];

const TYPE_COLOURS = {
  logo: "#0055FF",
  signature: "#7C3AED",
  social: "#EC4899",
  icon: "#F59E0B",
  banner: "#16A34A",
  other: "#525252",
};

export default function BrandAssetsPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {} for new | row for edit
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/brand-assets");
      setAssets(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load brand assets");
    }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openNew = () => { setEditor({ asset_type: "logo", sort_order: 0 }); setFile(null); };
  const openEdit = (a) => { setEditor({ ...a }); setFile(null); };
  const close = () => { setEditor(null); setFile(null); };

  const save = async () => {
    if (!editor) return;
    const isNew = !editor.id;
    if (isNew && !file) { toast.error("Pick an image file first"); return; }
    if (!editor.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", editor.name.trim());
        fd.append("asset_type", editor.asset_type || "other");
        fd.append("click_url", editor.click_url || "");
        fd.append("alt_text", editor.alt_text || "");
        fd.append("sort_order", editor.sort_order || 0);
        await api.post("/marketing/brand-assets", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Uploaded "${editor.name}"`);
      } else {
        await api.put(`/marketing/brand-assets/${editor.id}`, {
          name: editor.name,
          asset_type: editor.asset_type,
          click_url: editor.click_url || "",
          alt_text: editor.alt_text || "",
          sort_order: Number(editor.sort_order) || 0,
        });
        toast.success(`Updated "${editor.name}"`);
      }
      close();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Archive "${a.name}"? Existing emails that reference it will keep working.`)) return;
    try {
      await api.delete(`/marketing/brand-assets/${a.id}`);
      toast.success("Archived");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const copyUrl = (a) => {
    navigator.clipboard?.writeText(resolveBackendFileUrl(a.image_url));
    toast.success("Image URL copied");
  };

  return (
    <MarketingShell>
      <div className="max-w-[1400px] mx-auto px-6 py-6" data-testid="brand-assets-page">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-[#0A0A0A]">Brand Assets</h2>
            <p className="text-xs text-[#525252] mt-1 max-w-xl">
              Upload once, drop into any campaign or workflow from the <strong>Assets</strong> menu in the WYSIWYG editor.
              Each asset remembers its click-through URL, so inserting the LinkedIn icon automatically links to your LinkedIn profile.
            </p>
          </div>
          <button
            onClick={openNew}
            className="h-9 px-4 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC] flex items-center gap-1.5"
            data-testid="new-brand-asset-btn"
          >
            <Plus size={12} weight="bold" /> Upload asset
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-xs text-[#A3A3A3]">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="border border-dashed border-[#E5E5E5] bg-white py-16 text-center" data-testid="brand-assets-empty">
            <UploadSimple size={28} className="mx-auto text-[#A3A3A3] mb-2" />
            <p className="text-sm font-semibold text-[#525252]">No brand assets yet</p>
            <p className="text-xs text-[#A3A3A3] mt-1">Upload a logo or a social icon to get started.</p>
            <button onClick={openNew} className="mt-4 h-8 px-4 text-[11px] font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC]">
              + First asset
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="brand-assets-grid">
            {assets.map((a) => (
              <div key={a.id} className="group bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors">
                <div className="aspect-square bg-[#FAFAFA] flex items-center justify-center p-4 border-b border-[#F5F5F5] overflow-hidden">
                  <img src={resolveBackendFileUrl(a.image_url)} alt={a.alt_text || a.name}
                       className="max-w-full max-h-full object-contain"
                       data-testid={`brand-asset-thumb-${a.id}`} />
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-xs font-bold text-[#0A0A0A] truncate flex-1" title={a.name}>{a.name}</p>
                    <span
                      className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5"
                      style={{ backgroundColor: `${TYPE_COLOURS[a.asset_type] || "#525252"}14`, color: TYPE_COLOURS[a.asset_type] || "#525252" }}
                    >
                      {a.asset_type}
                    </span>
                  </div>
                  {a.click_url && (
                    <p className="text-[10px] text-[#525252] truncate mt-1 flex items-center gap-1" title={a.click_url}>
                      <LinkIconPh size={10} /> {a.click_url}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F5F5F5]">
                    <button onClick={() => copyUrl(a)}
                            className="text-[10px] font-semibold text-[#525252] hover:text-[#0055FF]"
                            data-testid={`brand-asset-copy-${a.id}`}>
                      Copy URL
                    </button>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => openEdit(a)}
                              className="p-1 text-[#525252] hover:text-[#0055FF]"
                              title="Edit"
                              data-testid={`brand-asset-edit-${a.id}`}>
                        <PencilSimple size={13} />
                      </button>
                      <button onClick={() => remove(a)}
                              className="p-1 text-[#525252] hover:text-[#DC2626]"
                              title="Archive"
                              data-testid={`brand-asset-delete-${a.id}`}>
                        <Trash size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && close()}>
            <div className="bg-white w-full max-w-xl border border-[#E5E5E5] shadow-2xl" onClick={e => e.stopPropagation()} data-testid="brand-asset-modal">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#0A0A0A]">
                  {editor.id ? "Edit asset" : "Upload asset"}
                </h3>
                <button onClick={() => !saving && close()} className="p-1 text-[#525252] hover:text-[#0A0A0A]" data-testid="brand-asset-modal-close">
                  <X size={16} />
                </button>
              </div>
              <div className="p-5 space-y-4 text-xs">
                {!editor.id && (
                  <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="border border-dashed border-[#E5E5E5] hover:border-[#0055FF] bg-[#FAFAFA] p-6 text-center cursor-pointer transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {file ? (
                      <div className="flex items-center justify-center gap-3">
                        <img src={URL.createObjectURL(file)} alt="preview" className="max-h-24 max-w-full" />
                        <div className="text-left">
                          <p className="font-bold text-[#0A0A0A] break-all">{file.name}</p>
                          <p className="text-[10px] text-[#A3A3A3]">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <UploadSimple size={28} className="mx-auto text-[#A3A3A3] mb-2" />
                        <p className="font-bold text-[#525252]">Drop image here or click to browse</p>
                        <p className="text-[10px] text-[#A3A3A3] mt-1">PNG, JPG, GIF, WebP, SVG — max 5 MB</p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="hidden"
                      data-testid="brand-asset-file-input"
                    />
                  </div>
                )}
                <label className="block">
                  <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Name <span className="text-[#DC2626]">*</span></span>
                  <input
                    value={editor.name || ""}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                    placeholder="e.g. LinkedIn icon, Jan signature"
                    className="mt-1 w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                    data-testid="brand-asset-name-input"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Type</span>
                    <select
                      value={editor.asset_type || "other"}
                      onChange={(e) => setEditor({ ...editor, asset_type: e.target.value })}
                      className="mt-1 w-full h-9 px-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                      data-testid="brand-asset-type-input"
                    >
                      {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Sort order</span>
                    <input
                      type="number"
                      value={editor.sort_order || 0}
                      onChange={(e) => setEditor({ ...editor, sort_order: e.target.value })}
                      className="mt-1 w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF] tabular-nums"
                      data-testid="brand-asset-sort-input"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Click-through URL (optional)</span>
                  <input
                    type="url"
                    value={editor.click_url || ""}
                    onChange={(e) => setEditor({ ...editor, click_url: e.target.value })}
                    placeholder="https://www.linkedin.com/company/eyvo"
                    className="mt-1 w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                    data-testid="brand-asset-click-input"
                  />
                  <span className="text-[10px] text-[#A3A3A3]">Social icons → profile URL. Logos usually have no link.</span>
                </label>
                <label className="block">
                  <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Alt text</span>
                  <input
                    value={editor.alt_text || ""}
                    onChange={(e) => setEditor({ ...editor, alt_text: e.target.value })}
                    placeholder="Accessibility description (falls back to Name)"
                    className="mt-1 w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                    data-testid="brand-asset-alt-input"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <button onClick={close} disabled={saving}
                        className="h-9 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#525252] disabled:opacity-40" data-testid="brand-asset-cancel">
                  Cancel
                </button>
                <button onClick={save} disabled={saving}
                        className="h-9 px-4 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-40" data-testid="brand-asset-save">
                  {saving ? "Saving…" : (editor.id ? "Save changes" : "Upload")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MarketingShell>
  );
}
